import CheckIcon from '@mui/icons-material/Check';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const CAForm109Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="CA Form 109 Instructions (2025): How to File & Due Dates"
          description="Everything you need to know about CA Form 109 for 2025, including who must file, due dates, extension options, filing steps, penalties, and common questions."
          keywords=""
          canonical="resources/tax-exempt/form-109-instructions/"
          ogType="article"
          jsonLdPricing="69.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> CA Form 109 </span>
          </span>{' '}
          Instructions (2025): Complete Filing Guide
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Complete your CA Form 109 accurately with step-by-step instructions,
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>
          filing requirements, due dates, and common mistakes to avoid.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=caform109instructions`}
          >
            Start Filing CA Form 109
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-109-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    CA FTB Form 109 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {' '}
                      <strong style={{ fontWeight: 600 }}>
                        CA FTB Form 109{' '}
                      </strong>{' '}
                      is used by California tax-exempt organizations to report
                      and pay tax on unrelated business taxable income (UBTI).
                      Organizations with{' '}
                      <strong style={{ fontWeight: 600 }}>
                        more than $1,000{' '}
                      </strong>{' '}
                      in gross income from an unrelated trade or business
                      generally must file Form 109.{' '}
                    </Typography>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Due Date: </strong>{' '}
                        Most organizations file by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          15th day of the 5th month
                        </strong>{' '}
                        after the tax year ends. Employees' trusts, IRAs, and
                        Coverdell ESAs generally file by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          15th day of the 4th month.
                        </strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extension:</strong>{' '}
                        An automatic{' '}
                        <strong style={{ fontWeight: 600 }}>
                          6-month extension
                        </strong>
                        is available, but any tax due must be paid by the
                        original due date.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Late Filing Penalties:{' '}
                        </strong>{' '}
                        Late filing or payment may result in 
                        <strong style={{ fontWeight: 600 }}>
                          FTB penalties and interest.{' '}
                        </strong>
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Box
                    component="div"
                    sx={{ mb: 5, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            mb: 1,
                            color: '#f8f9fa',
                            fontSize: 17,
                            lineHeight: 1.8,
                          }}
                        >
                          File your FTB Form 109 quickly and securely with
                          TaxZerone and enjoy 10% off — use code{' '}
                          <Box
                            component="span"
                            sx={{
                              p: '3px 8px',
                              backgroundColor: '#faedcd',
                              color: '#ff006f',
                              letterSpacing: 2,
                              border: '2px dashed #212121',
                              fontWeight: 600,
                            }}
                          >
                            {' '}
                            TZOTE10
                          </Box>{' '}
                          at checkout!
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': {
                              color: '#fff',
                              background: '#172b6b',
                            },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=caform109instructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                      mb: 0.5,
                      scrollMarginTop: 88,
                    }}
                    id="whatis"
                  >
                    What's New
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    The latest Form 109 instructions include these key
                    California tax and reporting updates.
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,

                      '& li': {
                        mb: 1.5,
                      },
                    }}
                  >
                    <li>
                      {' '}
                      <Box>
                        <span style={{ fontWeight: 600 }}>
                          FTB Form 4197 Reporting:
                        </span>{' '}
                        You may need to file FTB Form 4197, Information on Tax
                        Expenditure Items, with Form 109 if you have a
                        California R&TC Section 41 reporting requirement. Check
                        the Section 41 requirements to determine whether Form
                        4197 applies to your organization.{' '}
                      </Box>
                    </li>
                    <li>
                      {' '}
                      <Box>
                        <span style={{ fontWeight: 600 }}>
                          OBBBA Federal Tax Changes:
                        </span>{' '}
                        California generally does not conform to the federal tax
                        changes under the One Big Beautiful Bill Act (OBBBA).
                        Specific California state regulations might also be
                        applicable.{' '}
                      </Box>
                    </li>
                    <li>
                      {' '}
                      <Box>
                        <span style={{ fontWeight: 600 }}>
                          Program 4.0 Film Credit:
                        </span>{' '}
                        ffective from the beginning of the taxable year 2025,
                        the state of California adopted the Program 4.0 Motion
                        Picture and Television Production Credit, which includes
                        the refundable tax credit provision.{' '}
                      </Box>
                    </li>
                    <li>
                      {' '}
                      <Box>
                        <span style={{ fontWeight: 600 }}>
                          Other California Tax Changes:
                        </span>{' '}
                        In addition to that, there is an update for Financial
                        Institution Apportionment, Qualified Wildfire
                        Settlements Exclusion, and Chiquita Canyon Landfill
                        Event Payment Exclusion.{' '}
                      </Box>
                    </li>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents{' '}
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step-by-Step Instructions to Complete CA Form 109
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part1" sx={{ color: '#0202ff' }}>
                          Part I Unrelated Trade or Business Income
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#part2" sx={{ color: '#0202ff' }}>
                          Part II Deductions Not Taken Elsewhere
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule A Cost of Goods Sold and/or Operations.
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schb" sx={{ color: '#0202ff' }}>
                          Schedule B Tax Credits.
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schk" sx={{ color: '#0202ff' }}>
                          Schedule K Add-On Taxes or Recapture of Tax.
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schr" sx={{ color: '#0202ff' }}>
                          Schedule R Apportionment Formula Worksheet
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schc" sx={{ color: '#0202ff' }}>
                          Schedule C Rental Income from Real Property and
                          Personal Property Leased with Real Property
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schd" sx={{ color: '#0202ff' }}>
                          Schedule D Unrelated Debt-Financed Income
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sche" sx={{ color: '#0202ff' }}>
                          Schedule E Investment Income of an R&TC Section
                          23701g, Section 23701i, or Section 23701n Organization
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schf" sx={{ color: '#0202ff' }}>
                          Schedule F Interest, Annuities, Royalties and Rents
                          from Controlled Organizations
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schg" sx={{ color: '#0202ff' }}>
                          Schedule G Exploited Exempt Activity Income, other
                          than Advertising Income
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schh" sx={{ color: '#0202ff' }}>
                          Schedule H Advertising Income and Excess Advertising
                          Costs
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schi" sx={{ color: '#0202ff' }}>
                          Schedule I Compensation of Officers, Directors, and
                          Trustees
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schj" sx={{ color: '#0202ff' }}>
                          Schedule J Depreciation (Corporations and Associations
                          only. Trusts use form FTB 3885F.)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sources" sx={{ color: '#0202ff' }}>
                          Source and References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="step"
                >
                  Step-by-Step Instructions to Complete CA Form 109
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-details-img.webp" />
                  <source srcSet="/assets/images/ftbf109-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-details-img.png"
                    loading="lazy"
                    alt="FTB Form 109 organization details section showing tax year, organization name, FEIN, address."
                    width={760}
                    height={247}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 17, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Taxable Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the tax year for which you are filing California Form
                  109. If your organization uses a calendar year, enter the
                  beginning and ending dates for the calendar year. If your
                  organization follows a fiscal year, enter the applicable
                  beginning and ending dates in MM/DD/YYYY format.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Example
                </Typography>
                <Box component="div" sx={{ mt: 1, mb: 1.5 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderRadius: 2,
                    }}
                  >
                    <Table aria-label="Form 990 Penalty Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 220,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Filing Type
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 180,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Beginning Date
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 220,
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Ending Date
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Calendar Year
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            01/01/2025
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            12/31/2025
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Fiscal Year
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            07/01/2025
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            06/30/2026
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> The tax
                  year entered on Form 109 should match the tax year reported on
                  your federal tax return.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Corporation/Organization Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your organization's legal name exactly as it appears in
                  the records of the IRS and the California Franchise Tax Board
                  (FTB). Do not use abbreviations, nicknames, or trade names
                  unless they are part of the registered legal name.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 2,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example :</strong>
                  <br />{' '}
                  <span style={{ fontWeight: 600 }}>
                    Corporation/Organization Name:
                  </span>
                  &nbsp;Helping Hands Foundation, Inc.
                </Alert>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Tips:</strong> Verify the
                  organization's name before filing to help avoid processing
                  delays.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  California Corporation Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the California entity number or identification number
                  assigned to your organization by the California Secretary of
                  State or the California Franchise Tax Board (FTB).
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 2,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br />{' '}
                  <span style={{ fontWeight: 600 }}>
                    California Corporation Number :
                  </span>
                  &nbsp;C1234567
                </Alert>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Where to find it:</span>{' '}
                  Corporation/Organization Name : &nbsp;Previous California tax
                  returns, FTB notices, or California Secretary of State
                  registration documents.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Additional Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete this field only if the California Form 109
                  instructions specifically request additional identifying
                  information. Otherwise, leave it blank.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 4,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> Additional Information: Name change effective
                  01/01/2025
                </Alert>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Most
                  organizations can leave this field blank.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Federal Employer Identification Number (FEIN)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter your organization's nine-digit Federal Employer
                  Identification Number (EIN) assigned by the IRS.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 2,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> <span style={{ fontWeight: 600 }}>FEIN:</span>
                  &nbsp;12-3456789
                </Alert>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Tips:</strong> Ensure the
                  FEIN matches your IRS records to avoid filing issues.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Street Address
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your organization's complete mailing address, including
                  the street number, street name, suite or room number (if
                  applicable), or PO Box.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br />{' '}
                  <span style={{ fontWeight: 600 }}>Street Address:</span>
                  123 Main Street, Suite 200
                </Alert>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Use the
                  mailing address where you want to receive correspondence from
                  the California Franchise Tax Board.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  PMB Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization uses a{' '}
                  <span style={{ fontWeight: 600 }}>
                    Private Mailbox (PMB),
                  </span>{' '}
                  enter the mailbox number. If not, leave this field blank.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example :</strong>
                  <br /> <span style={{ fontWeight: 600 }}>PMB Number:</span>
                  &nbsp;PMB 205
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  City (If the corporation has a foreign address):
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the city for your organization's mailing address.
                    </Box>
                  </li>

                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      If your organization has a foreign mailing address,
                      complete the foreign address fields instead.
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> <span style={{ fontWeight: 600 }}>City:</span>
                  &nbsp;Sacramento
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  State
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the two-letter abbreviation for the state where your
                  mailing address is located.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> <span style={{ fontWeight: 600 }}>State:</span>
                  &nbsp;CA
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  ZIP Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the five-digit or nine-digit ZIP code for your mailing
                  address.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> <span style={{ fontWeight: 600 }}>ZIP Code: </span>
                  &nbsp;95814 or 95814-1234
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Foreign Country Name:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization has a mailing address outside the United
                  States, enter the full name of the country.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br />{' '}
                  <span style={{ fontWeight: 600 }}>Foreign Country:</span>
                  &nbsp;Canada
                </Alert>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Leave this
                  field blank if your organization has a U.S. mailing address.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Foreign Province, State, or County
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the province, state, territory, or county associated
                  with your foreign mailing address.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br />{' '}
                  <span style={{ fontWeight: 600 }}>Province/State:</span>
                  &nbsp;Ontario
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Foreign Postal Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the postal code assigned to your foreign mailing
                  address.
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br />{' '}
                  <span style={{ fontWeight: 600 }}>Foreign Postal Code:</span>
                  &nbsp;M5V 3L9
                </Alert>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 2,
                    mt: 4,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Do not
                  enter a U.S. ZIP code in this field.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/ftbf109-organization-img.webp" />
                  <source srcSet="/assets/images/ftbf109-organization-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-organization-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Organization Information section with filing status, accounting method, business activity, trust, tax benefits, and hospital details."
                    width={760}
                    height={202}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Box component="div" sx={{ mt: 1, mb: 1.5 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderRadius: 2,
                    }}
                  >
                    <Table aria-label="Quick Reference">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: '#f1f1f1',
                            borderbottom: '1px solid #828282',
                          }}
                        >
                          <TableCell
                            colSpan={3}
                            sx={{
                              color: '#444',
                              borderBottom: '1px solid #828282',
                              fontSize: 20,
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Quick Reference
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 220,
                              borderRight: '1px solid #828282',

                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Questions
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 180,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            Section
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 220,
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            What it covers
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            A, C, D, E
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Return status
                          </TableCell>

                          <TableCell>
                            Is this the first return, an amendment, a final
                            filing, or is the org under audit?
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            F, G
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Accounting and business
                          </TableCell>

                          <TableCell>
                            How the org keeps its books and what unrelated
                            business activity it conducts
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            B, H, I, J, K, L
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Special classifications
                          </TableCell>
                          <TableCell>
                            Whether the org falls into a specific legal or tax
                            category that requires additional attachments
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  A: First Return Filed?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" only if this is the very first time your
                  organization is filing Form 109 with California. Leave blank
                  or check "No" for all subsequent years.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  B: Education IRA
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" if your organization qualifies as an education IRA
                  under California Revenue & Taxation Code Section 23712. Most
                  nonprofits will check "No."
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  C: IRS Audit Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" if the IRS is currently auditing your
                  organization, or audited it in any prior year. California uses
                  this to cross-check federal audit outcomes.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  D: Final Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" if the organization is closing. Select the reason
                  - Dissolved, Surrendered (Withdrawn), or Merged/Reorganized -
                  and enter the date (mm/dd/yyyy) the closure took effect.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  E: Amended Return?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check " Yes " if you are correcting or changing a previously
                  filed Form 109 for the same tax year; otherwise, select " No".
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  F: Accounting Method
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Mark the method your organization uses to record income and
                  expenses:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                    },
                  }}
                >
                  <li>
                    (1)
                    <Box>Cash - record when money changes hands.</Box>
                  </li>

                  <li>
                    (2)
                    <Box>Accrual - record when earned or incurred</Box>
                  </li>
                  <li>
                    (3)
                    <Box>Other - describe in an attachment.</Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  G: Nature of Trade or Business
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write a brief, plain-English description of the unrelated
                  business activity that generated taxable income - for example,
                  "advertising sales" or "rental of commercial property."
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  H: Non-Exempt Charitable Trust
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" if your organization is a non-exempt charitable
                  trust described under IRC Section 4947(a)(1). These trusts are
                  treated similarly to private foundations for tax purposes.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  I: Tax Benefits Claimed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" if your organization is claiming tax benefits
                  under any of California's former incentive programs:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                    },
                  }}
                >
                  <li>
                    <Box>EZ - Enterprise Zone</Box>
                  </li>

                  <li>
                    <Box>LAMBRA - Local Agency Military Base Recovery Area</Box>
                  </li>
                  <li>
                    <Box>TTA - Targeted Tax Area</Box>
                  </li>
                  <li>
                    <Box>MEA - Manufacturing Enhancement Area</Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  J: Qualified Pension or Profit-Sharing Plan
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" if the organization is a qualified pension,
                  profit-sharing, or stock bonus plan under IRC Section 401(a).
                  This affects how unrelated business income is taxed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  K: Unrelated Business Activity (UBA) Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the code that best describes the organization’s
                  unrelated business activity reported on Form 109.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  L: Is this Hospital?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check "Yes" if your organization is a hospital. If so, you
                  must attach federal Schedule H (Form 990), which reports
                  community benefit activities and financial assistance
                  policies.
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mb: 4,
                    mt: 4,
                    borderLeft: '3px solid #0288d1',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Important : </strong>
                  &nbsp;If Schedule H is not attached, the return will be
                  rejected.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/ftbf109-line1-26-img.webp" />
                  <source srcSet="/assets/images/ftbf109-line1-26-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-line1-26-img.png"
                    loading="lazy"
                    alt="FTB California unrelated business income tax form, lines 1–26, covering income calculation, tax computation, payments, and balance due."
                    width={760}
                    height={437}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Taxable Corporation
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the unrelated business taxable income calculated on Side
                  2, Part II, Line 30.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 1 by the average apportionment percentage from
                  Schedule R. This applies if your organization does business
                  both inside and outside California.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the lesser amount of Line 1 or Line 2. If Schedule R was
                  not completed, enter the amount from Line 1 directly.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Taxable Trust
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Same as Line 1 above but applies to charitable trusts. Pull
                  the figure from Side 2, Part II, Line 30.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Tax Computation
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 5:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter unrelated business taxable income from Line 3 or Line 4,
                  depending on whether you are a corporation or a charitable
                  trust.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 6:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Deduct any Net Operating Loss carryover from Enterprise Zone,
                  LAMBRA, or Targeted Tax Area programs. See General Information
                  N.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 7:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Net Operating Loss deduction. See General
                  Information N for eligibility and limits.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 8:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine the deductions from Lines 6 and 7.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 9:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 8 from Line 5. This is your taxable income after
                  NOL deductions.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 10:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Line 9 by the tax rate (shown as a percentage on the
                  form).
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 11:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any applicable tax credits from Schedule B.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Total Tax
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 12:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 11 from Line 10. If Line 11 is greater than Line
                  10, enter 0.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 13:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  See General Information O for AMT calculation details.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 14:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 12 and 13. This is your total tax owed.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Payments
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 15:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any overpayment from a prior year allowed as a credit
                  toward this year's tax.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 16:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter total estimated tax payments made during 2025.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 17:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter California withholding amounts from Form 592-B and/or
                  Form 593.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 18:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    refundable Program 4.0 credit amount
                  </span>{' '}
                  if you made the one-time irrevocable election to receive the
                  credit as a refund because the credit exceeds your California
                  tax liability.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 19:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any payment made when you filed your extension using
                  Form FTB 3539.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 20:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Lines 15 through 19. This is the total of all your
                  payments and credits.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Use Tax / Tax Due / Overpayment
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 21:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any California use tax owed. See instructions.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 22:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Line 20 is more than Line 21, subtract Line 21 from Line 20
                  to find your net payments available.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 23:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Line 21 is more than Line 20, subtract Line 20 from Line
                  21. This is the additional use tax you owe.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 24:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of tax you owe after subtracting Line 22 from
                  Line 14. If the result is more than zero, pay the full amount
                  with your return.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 25:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 14 from Line 22. If this is positive, you have
                  overpaid.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 26:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Choose how much of your overpayment (Line 25) you want carried
                  forward and applied to next year's estimated tax.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-line27-img.webp" />
                  <source srcSet="/assets/images/ftbf109-line27-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-line27-img.png"
                    loading="lazy"
                    alt="FTB form 109 lines 27-30, covering Refund or Amount Due."
                    width={760}
                    height={91}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Refund or Amount Due
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 27:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If&nbsp;
                  <span style={{ fontWeight: 600 }}>
                    Line 25 is greater than Line 26
                  </span>
                  , subtract <span style={{ fontWeight: 600 }}>Line 26</span>,
                  from <span style={{ fontWeight: 600 }}>Line 25</span>.&nbsp;
                  Enter the result as your{' '}
                  <span style={{ fontWeight: 600 }}>California tax refund</span>
                  .&nbsp; If you choose direct deposit, complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    Lines 27a through 27c
                  </span>{' '}
                  with your bank account information.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 27a:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your bank’s{' '}
                  <span style={{ fontWeight: 600 }}>
                    9-digit routing number
                  </span>
                  , if you want your refund deposited directly into your
                  checking or savings account.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 27b:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select the type of bank account where you want your refund
                  deposited by checking either{' '}
                  <span style={{ fontWeight: 600 }}>Checking</span>, or{' '}
                  <span style={{ fontWeight: 600 }}>Savings</span>.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 27c:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>account number</span> for
                  the checking or savings account that will receive your direct
                  deposit refund. Make sure the account number matches the
                  account selected on{' '}
                  <span style={{ fontWeight: 600 }}>Line 27b.</span>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 28:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    penalties and interest
                  </span>{' '}
                  you owe, if applicable. Refer to the{' '}
                  <span style={{ fontWeight: 600 }}>General Information</span>{' '}
                  section to determine the correct amount.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 29:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the box only if your{' '}
                  <span style={{ fontWeight: 600 }}>estimated</span> tax penalty
                  was calculated using{' '}
                  <span style={{ fontWeight: 600 }}>Exception B</span> or{' '}
                  <span style={{ fontWeight: 600 }}>Exception C</span>. Attach{' '}
                  <span style={{ fontWeight: 600 }}>Form FTB 5806</span> when
                  required.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 30:
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you owe additional tax, calculate your{' '}
                  <span style={{ fontWeight: 600 }}>total amount due by</span>{' '}
                  adding{' '}
                  <span style={{ fontWeight: 600 }}>
                    Lines 23, 24, 26, and 28
                  </span>
                  , then subtract{' '}
                  <span style={{ fontWeight: 600 }}>Line 25</span>. Enter the
                  result on <span style={{ fontWeight: 600 }}>Line 30</span>
                  .{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Unrelated Business Taxable Income
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="part1"
                >
                  Part I Unrelated Trade or Business Income
                </Typography>

                <picture>
                  <source srcSet="/assets/images/ftbf109-part1-unrelated-img.webp" />
                  <source srcSet="/assets/images/ftbf109-part1-unrelated-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-part1-unrelated-img.png"
                    loading="lazy"
                    alt="FTB Unrelated Business Taxable Income, Part I, lines 1–13, covering gross receipts, deductions, and total unrelated trade or business income. All fields blank."
                    width={760}
                    height={289}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your organization's{' '}
                  <span style={{ fontWeight: 600 }}>gross receipts</span> or{' '}
                  <span style={{ fontWeight: 600 }}>gross sales</span> from
                  unrelated business activities. Do not subtract returns,
                  allowances, or business expenses on this line.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total{' '}
                  <span style={{ fontWeight: 600 }}>
                    returns and allowances
                  </span>{' '}
                  related to the gross receipts reported on{' '}
                  <span style={{ fontWeight: 600 }}>Line 1</span>. This reduces
                  the gross receipts before calculating gross profit.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>Line 2</span> from{' '}
                  <span style={{ fontWeight: 600 }}>Line 1</span> and enter the
                  result as your{' '}
                  <span style={{ fontWeight: 600 }}>gross profit</span>. If you
                  report the cost of goods sold, complete the required federal
                  schedule before calculating this line.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>capital gain</span> or{' '}
                  <span style={{ fontWeight: 600 }}>capital loss</span> from
                  unrelated business activities. Include the amount reported on
                  the applicable federal schedule, if required.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 5:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>ordinary gains</span> or{' '}
                  <span style={{ fontWeight: 600 }}>losses</span> from the sale,
                  exchange, or disposition of business property. Attach the
                  required federal schedules when applicable.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 6:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>interest income</span>{' '}
                  received from unrelated business activities.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 7:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>dividend income</span>{' '}
                  received from unrelated business activities.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 8:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter
                  <span style={{ fontWeight: 600 }}> rental income </span> from
                  real property, personal property, or debt-financed property
                  that is subject to unrelated business income tax.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 9:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    interest, annuities, royalties, or rents
                  </span>{' '}
                  received from controlled organizations.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 10:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    exploited exempt activity income
                  </span>{' '}
                  reported on the applicable schedule.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 11:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter all other{' '}
                  <span style={{ fontWeight: 600 }}>business income</span> that
                  has not been reported on{' '}
                  <span style={{ fontWeight: 600 }}>Lines 1 through 10</span>.{' '}
                  Include only income subject to unrelated business income tax.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 12:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>Lines 3 through 11</span>{' '}
                  and enter the total{' '}
                  <span style={{ fontWeight: 600 }}>
                    gross unrelated business income
                  </span>
                  .
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 13:
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>total deductions</span>{' '}
                  directly connected with the unrelated business income reported
                  on
                  <span style={{ fontWeight: 600 }}>Line 12</span>. Do not
                  include charitable contributions, dividends received
                  deductions, or trust deductions claimed elsewhere on the
                  return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="part2"
                >
                  Part II Deductions Not Taken Elsewhere
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-part2-deduction-img.webp" />
                  <source srcSet="/assets/images/ftbf109-part2-deduction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-part2-deduction-img.png"
                    loading="lazy"
                    alt="FTB Unrelated Business Taxable Income, Part II, lines 14–30, covering deductions and final unrelated business taxable income."
                    width={760}
                    height={319}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 14:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>Line 13</span> from{' '}
                  <span style={{ fontWeight: 600 }}>Line 12</span> and enter the
                  result as your{' '}
                  <span style={{ fontWeight: 600 }}>
                    unrelated business taxable income before specific deductions
                  </span>
                  .
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 15:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any allowable{' '}
                  <span style={{ fontWeight: 600 }}>salaries and wages</span>{' '}
                  directly connected with the unrelated business activity that
                  have not already been deducted elsewhere.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 16:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter allowable{' '}
                  <span style={{ fontWeight: 600 }}>
                    repair and maintenance expenses
                  </span>{' '}
                  directly related to the unrelated business activity.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 17:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any <span style={{ fontWeight: 600 }}>bad debts</span>{' '}
                  that are deductible for the unrelated business activity.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 18:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter allowable{' '}
                  <span style={{ fontWeight: 600 }}>interest expense</span>{' '}
                  directly connected with the unrelated business activity.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 19:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any <span style={{ fontWeight: 600 }}>taxes</span> that
                  are deductible for the unrelated business activity.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 20:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter allowable{' '}
                  <span style={{ fontWeight: 600 }}>
                    contributions, deductions, and adjustments
                  </span>{' '}
                  reported on the applicable schedules.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 21a:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization has a{' '}
                  <span style={{ fontWeight: 600 }}>
                    net operating loss (NOL) carryforward
                  </span>
                  , enter the allowable deduction from{' '}
                  <span style={{ fontWeight: 600 }}>Schedule A, Part II</span>.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 21b:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If applicable, enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    charitable contributions deduction
                  </span>{' '}
                  from <span style={{ fontWeight: 600 }}>Schedule A</span>.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 22:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any allowable{' '}
                  <span style={{ fontWeight: 600 }}>depletion deduction</span>{' '}
                  related to the unrelated business activity.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 23:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any allowable{' '}
                  <span style={{ fontWeight: 600 }}>contributions</span>{' '}
                  reported on the applicable schedule.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 24:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any other allowable{' '}
                  <span style={{ fontWeight: 600 }}>deductions</span> that have
                  not been reported on previous lines.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 25:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>Lines 14 through 24</span>{' '}
                  and enter the total{' '}
                  <span style={{ fontWeight: 600 }}>allowable deductions</span>.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 26:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>Line 25</span> from{' '}
                  <span style={{ fontWeight: 600 }}>Line 12</span> and enter the
                  result as your{' '}
                  <span style={{ fontWeight: 600 }}>
                    unrelated business taxable i
                  </span>
                  .
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 27:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the allowable{' '}
                  <span style={{ fontWeight: 600 }}>specific deduction</span>{' '}
                  for unrelated business taxable income. Subtract this amount
                  from <span style={{ fontWeight: 600 }}>Line 26</span>.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 28:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any allowable{' '}
                  <span style={{ fontWeight: 600 }}>business tax credits</span>,
                  if applicable.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 29:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract any applicable credits from the calculated tax and
                  enter the remaining{' '}
                  <span style={{ fontWeight: 600 }}>tax due</span>.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 30:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the final{' '}
                  <span style={{ fontWeight: 600 }}>
                    California unrelated business income tax due
                  </span>{' '}
                  after applying all deductions, credits, and adjustments.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-signature-img.webp" />
                  <source srcSet="/assets/images/ftbf109-signature-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-signature-img.png"
                    loading="lazy"
                    alt="IRS form signature section showing spaces for officer and preparer signatures, title, date, telephone, PTIN, firm name, and FEIN."
                    width={760}
                    height={199}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Sign Here
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the California entity number or identification number
                  assigned to your organization by the California Secretary of
                  State or the California Franchise Tax Board (FTB).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Signature of Officer:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The organization&apos;s{' '}
                  <span style={{ fontWeight: 600 }}>authorized officer</span>{' '}
                  must sign the return. By signing, the officer declares under
                  penalty of perjury that the return and all supporting
                  schedules are accurate and complete.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Title:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>official title</span> of the
                  individual signing the return, such as{' '}
                  <span style={{ fontWeight: 600 }}>
                    President, Treasurer, Chief Financial Officer,
                  </span>{' '}
                  or another authorized position.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Date:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the authorized officer signs the return using
                  the required date format.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Telephone:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the daytime telephone number of the authorized officer
                  in case the Franchise Tax Board (FTB) needs additional
                  information about the return.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Paid Preparer's Use Only
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete this section only if someone other than the
                  organization's authorized officer prepared the return for
                  compensation.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Preparer's Name:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full name of the paid tax preparer who completed the
                  return.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Preparer's Signature:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The paid preparer must sign the return after it has been
                  completed.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Date:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the paid preparer signs the return.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Check if Self-Employed:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the paid preparer is{' '}
                  <span style={{ fontWeight: 600 }}>self-employed</span>.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  PTIN:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the preparer's{' '}
                  <span style={{ fontWeight: 600 }}>
                    Preparer Tax Identification Number (PTIN)
                  </span>{' '}
                  issued by the Internal Revenue Service (IRS).
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Firm's Name (or Yours, if Self-Employed) and Address:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name and mailing address of the preparer's firm. If
                  the preparer is self-employed, enter the preparer's business
                  name and address.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Firm's FEIN:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the firm's{' '}
                  <span style={{ fontWeight: 600 }}>
                    Federal Employer Identification Number (FEIN)
                  </span>
                  , if applicable.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Telephone:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the business telephone number for the preparer or
                  preparer's firm.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  May the FTB Discuss This Return with the Preparer Shown Above?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}>Yes</span> if you
                  authorize the California Franchise Tax Board (FTB) to discuss
                  this return with the paid preparer listed above.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}>No</span> if you do
                  not want the FTB to discuss your return with the preparer.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  This authorization allows the FTB to contact the preparer
                  regarding questions about the return but does{' '}
                  <span style={{ fontWeight: 600 }}>not</span> authorize the
                  preparer to receive refund payments or represent the
                  organization in all tax matters.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="scha"
                >
                  Schedule A – Cost of Goods Sold and/or Operations
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Complete this schedule only if your organization has a cost of
                  goods sold or cost of operations related to its unrelated
                  business activity.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shca-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shca-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shca-img.png"
                    loading="lazy"
                    alt="TB Form 109 Schedule A – Cost of Goods Sold and/or Operations, lines 1–7: inventory, purchases, labor, costs, and ending inventory."
                    width={760}
                    height={187}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of your inventory at the beginning of the tax
                  year.
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Tips :</strong> Use the
                  ending inventory amount reported on the previous year's return
                  if it applies.
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total cost of merchandise, materials, or supplies
                  purchased during the tax year for resale or production.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the direct labor costs incurred to manufacture or
                      produce goods sold during the year.
                    </Box>
                  </li>

                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Do not include salaries related to administrative or
                      office employees.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any additional costs that must be capitalized under IRC
                  Section 263A. If required, attach a supporting schedule
                  showing the calculation.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 5:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines <span style={{ fontWeight: 600 }}>1 through 4</span>{' '}
                  and enter the total.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 6 – Inventory at End of Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of inventory remaining at the end of the tax
                  year.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 7:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>Line 6</span> from{' '}
                  <span style={{ fontWeight: 600 }}>Line 5</span> and enter the
                  result.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  IRC Section 263A:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether the rules of{' '}
                  <span style={{ fontWeight: 600 }}>IRC Section 263A</span>{' '}
                  require you to include additional costs in inventory.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select <span style={{ fontWeight: 600 }}>Yes</span> or{' '}
                  <span style={{ fontWeight: 600 }}>No</span>.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schb"
                >
                  Schedule B – Tax Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete this schedule B only if your organization is claiming
                  California tax credits.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shcb-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shcb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shcb-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule B - Tax Credits, lines 1–4: tax credit names and codes, and total claimed tax credits."
                    width={760}
                    height={97}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of the tax credit you are claiming.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter another credit, if applicable.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter a third credit, if applicable.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add all allowable credits from Lines{' '}
                  <span style={{ fontWeight: 600 }}>1 through 3</span> and enter
                  the total of all claimed credits, on line 4.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schk"
                >
                  Schedule K – Add-On Taxes or Recapture of Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete this schedule if additional California taxes or
                  credit recaptures apply to your organization.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shck-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shck-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shck-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule K - Add-On Taxes or Recapture of Tax, lines 1–5: long-term contracts, installment sales, IRC Section 197 election, credit recapture, and total tax."
                    width={760}
                    height={115}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the interest amount computed under the long-term
                  contract look-back method, if applicable.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any interest on tax attributable to installment sales.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any required recapture amount resulting from an IRC
                  Section 197 election.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any California credit recapture required for the tax
                  year.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 5:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}> Lines 1 through 4</span>{' '}
                  and enter the total.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schr"
                >
                  Schedule R – Apportionment Formula Worksheet
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete Schedule R only if your organization carries on
                  unrelated trade or business activities both within and outside
                  California. Use this schedule to calculate the percentage of
                  income that is taxable by California.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shcr-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shcr-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shcr-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule R - Apportionment Formula Worksheet, Part A and Part B: sales, property, payroll, sales factors, and apportionment percentage."
                    width={760}
                    height={266}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part A: Standard Method – Single-Sales Factor Formula
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (a) – Total Within and Outside California
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total gross receipts or sales from your unrelated
                  trade or business activities, regardless of where they were
                  earned. Include sales from California and all other states or
                  countries.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (b) – Total Within California
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter only the gross receipts or sales generated from business
                  activities conducted within California.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (c) – Percent Within California
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the percentage of sales attributable to California
                  by dividing the amount in{' '}
                  <strong style={{ fontWeight: 600 }}> Column (b)</strong> by
                  the amount in{' '}
                  <strong style={{ fontWeight: 600 }}> Column (a)</strong>.
                  Multiply the result by{' '}
                  <strong style={{ fontWeight: 600 }}>100</strong> and enter the
                  percentage.
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Formula: Column (b) Ã· Column (a) Ã— 100
                  </Typography>
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> <span style={{ fontWeight: 600 }}>Calculation:</span>
                  &nbsp;$325,000 Ã· $850,000 Ã— 100
                  <br /> <span style={{ fontWeight: 600 }}>Result:</span>
                  &nbsp;38.24%
                </Alert>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 2,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Transfer
                  this percentage to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 109, Side 1, Line 2,
                  </span>{' '}
                  if required.
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your total sales from all locations during the tax year.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the sales attributable to California.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide California sales by total sales and enter the
                  percentage.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '20px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part B – Three-Factor Formula
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete Part B only if you are required to use the
                  three-factor apportionment method.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 1 – Property Factor
                </Typography>

                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Column
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            What to Enter
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Example
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Total within and outside California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Enter the average value of all business property
                            used during the tax year.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            $500,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Total within California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Enter the average value of property located in
                            California.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            $180,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Percent within California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Divide the California property by total property.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            36.00%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 2 – Payroll Factor
                </Typography>

                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Column
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            What to Enter
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Example
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Total within and outside California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Enter the total compensation paid to all employees
                            during the tax year.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            $250,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Total within California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Enter compensation paid to employees working in
                            California.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            $100,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Percent within California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Divide California payroll by total payroll.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            40.00%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 3 – Sales Factor
                </Typography>

                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Column
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            What to Enter
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Example
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Total within and outside California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Enter total gross receipts from all business
                            locations.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            $800,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Total within California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Enter only California gross receipts.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            $300,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Percent within California
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Divide California sales by total sales.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            37.50%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 4 – Total percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the percentages in column (c)
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Line 5 – Average Apportionment Percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Divide the amount on Line 4 by 3 and enter the result on this
                  line and Form 109, Side 1, Line 2. If one or more
                  apportionment factors have zero amounts in both columns,
                  divide by the number of factors that have amounts instead.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schc"
                >
                  Schedule C – Rental Income from Real Property and Personal
                  Property Leased with Real Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete Schedule C if your organization received income from
                  real property that is treated as unrelated business taxable
                  income.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shcc-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shcc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shcc-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule C - Rental Income,  rental property description, rent received or accrued, and percentage attributable to personal property."
                    width={760}
                    height={207}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (a): Description of the Property
                </Typography>
                <Typography variant="body1">
                  In Column (a), describe each property that generated rental
                  income during the tax year. Enter enough information to
                  identify the property, such as:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Type of property</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Property description</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Location or other identifying information</Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> If your organization rented out an office building, you
                  could enter:
                  <br />{' '}
                  <span style={{ fontWeight: 600 }}>
                    Commercial office building – Los Angeles, CA{' '}
                  </span>
                </Alert>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 2,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If you have
                  multiple rental properties, use a separate row for each
                  property.
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (b): Rent Received or Accrued
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    rent received or accrued
                  </span>{' '}
                  for the property during the tax year .Use the amount
                  applicable to the property identified in Column (a).
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If the
                  organization received or accrued $80,000 in rent from an
                  office building.
                  <br /> <span style={{ fontWeight: 600 }}>Enter: </span>$80,000
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (c): Percentage of Rent Attributable to Personal
                  Property
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the percentage of the total rent attributable to
                      personal property leased with the real property.{' '}
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Personal property may include items such as furniture,
                      equipment, or other movable property leased together with
                      the real property.{' '}
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> Suppose:
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,
                      listStyle: 'none',
                      p: 0,
                      '& li': {
                        mb: 1.5,
                        display: 'flex',
                        gap: 1,
                        ml: 2,
                      },
                    }}
                  >
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Total rent = $100,000</Box>
                    </li>
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>
                        Rent attributable to personal property = $20,000
                      </Box>
                    </li>
                  </Box>
                  The percentage is:{' '}
                  <span style={{ fontWeight: 600 }}> 20%</span> <br />
                  Enter <span style={{ fontWeight: 600 }}>20%</span> in Column
                  (c).
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is the{' '}
                  <span style={{ fontWeight: 600 }}>
                    most important decision point in Schedule C
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use the percentage in Column (c) to determine which section
                  applies.
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Personal-property portion
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Treatment
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            10% or less
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            Generally, the personal-property rent is not taxable
                            under this Schedule C rule
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            More than 10% but not more than 50%
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            The{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              personal-property portion of the rent
                            </span>{' '}
                            is taxable, subject to the applicable exclusions
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            More than 50%
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              All rents from real property and personal property
                            </span>
                            are taxable, subject to the applicable rules
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Rent depends on income or profits
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            The applicable rental income is taxable under the
                            more-than-50% rule, subject to the exception for
                            fixed percentage(s) of receipts or sales
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Important:</strong> When
                  personal-property rent is more than 10% but not more than 50%,
                  the FTB instructions state that the rents attributable to the
                  real property are generally not taxable, except where the
                  applicable exclusions/rules provide otherwise.
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (d):
                </Typography>
                <Typography variant="body1">
                  Complete Section (d) when:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      More than 50% of total rent is attributable to personal
                      property; or
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      The determination of rent depends in whole or in part on
                      the income or profits derived from the leased property,
                      subject to the fixed-percentage exception described by the
                      FTB.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (d)(i) - Deductions Directly Connected
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter deductions that are directly connected with the
                      rental income.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      If the form requires a supporting schedule, attach it as
                      instructed.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (d)(ii) - Income Includible
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Determine the income included by reducing the applicable
                      rental income by the directly connected deductions.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>This amount contributes to the Schedule C total.</Box>
                  </li>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (e):
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      If the personal-property portion is{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        more than 10% but not more than 50%,
                      </span>{' '}
                      use <span style={{ fontWeight: 600 }}>Section (e)</span>.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      {' '}
                      This section determines the taxable income attributable to
                      the personal property.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (e)(i) - Gross Income Reportable
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Calculate the gross income attributable to personal property.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Formula
                </Typography>
                <Typography variant="body1">
                  Column (b) Ã— Column (c)
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> If:
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,
                      listStyle: 'none',
                      p: 0,
                      '& li': {
                        mb: 1.5,
                        display: 'flex',
                        gap: 1,
                        ml: 2,
                      },
                    }}
                  >
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Rent = $100,000 </Box>
                    </li>
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Personal property percentage = 20%</Box>
                    </li>
                  </Box>
                  Then:
                  <br />
                  <span style={{ fontWeight: 600 }}>
                    $100,000 Ã— 20% = $20,000{' '}
                  </span>{' '}
                  <br />
                  Enter: <span style={{ fontWeight: 600 }}> $20,000</span>
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (e)(ii):
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter deductions directly connected with the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    personal property portion
                  </strong>{' '}
                  of the rental activity.{' '}
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <br /> If directly connected personal-property expenses are
                  $5,000:
                  <br />
                  <span style={{ fontWeight: 600 }}>Enter: $5,000</span>{' '}
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (e)(iii):
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtract the deductions in Column (e)(ii) from the gross
                  income in Column (e)(i).
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Example:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Calculation
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Gross personal-property income
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            $20,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                            }}
                          >
                            Less: directly connected deductions
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            $5,000
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Net income includible
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                              fontWeight: 600,
                            }}
                          >
                            $15,000
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter <span style={{ fontWeight: 600 }}> $15,000</span> in
                  Column (e)(iii).
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  After completing the applicable rows, add:{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Column (d)(ii) + Column (e)(iii){' '}
                  </strong>
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the total on{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Schedule C, Row 4.
                      </strong>{' '}
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      The form then instructs you to enter this amount on:{' '}
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      {' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Form 109, Side 2, Part I, Line 6 - Rental Income
                        (Schedule C).
                      </strong>{' '}
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schd"
                >
                  Schedule D Unrelated Debt-Financed Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Schedule D to report income from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    debt-financed property
                  </strong>
                  . The FTB defines debt-financed property as property held to
                  produce income when acquisition indebtedness existed at any
                  time during the tax year. When completing Schedule D, use the
                  federal{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-T instructions
                  </Link>{' '}
                  and apply California amounts where California and federal
                  rules differ.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shcd-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shcd-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shcd-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule D – Unrelated Debt-Financed Income, property, income, deductions, and net income."
                    width={760}
                    height={213}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                      mt: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (a):
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter a description of the debt-financed property that
                  generates the income.{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      For real property, enter the property address and a brief
                      description. For other properties, provide enough
                      information to identify the property.{' '}
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      If you have more than one debt-financed property, enter
                      each property on a separate row.{' '}
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> 123 Main
                  St., Los Angeles, CA – Office Building
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (b):
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter The gross income received from, or allocable to, the
                      debt-financed property for the tax year.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the gross income attributable to the property{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        before applying the debt-basis percentage
                      </strong>
                      .
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If the
                  property generated $100,000 of rental income <br />
                  <strong style={{ fontWeight: 600 }}> Enter: $100,000 </strong>
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (c)(i):
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the amount of straight-line depreciation directly
                      connected with or allocable to the debt-financed property.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Calculate the applicable depreciation amount using the
                      required depreciation records and{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        attach the supporting schedule
                      </strong>{' '}
                      as instructed.
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If the
                  allowable straight-line depreciation is $20,000 <br />
                  <strong style={{ fontWeight: 600 }}> Enter: $20,000 </strong>
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (c)(ii):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter other deductions that are{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    directly connected with or allocable to the debt-financed
                    property
                  </strong>
                  , excluding the straight-line depreciation already entered in
                  Column (c)(i).
                </Typography>
                <Typography variant="body1">
                  Examples may include applicable:
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Repairs</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Maintenance</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Insurance</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Property-related expenses</Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If
                  applicable other deductions total $10,000
                  <br />
                  <strong style={{ fontWeight: 600 }}> Enter: $10,000 </strong>
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (d):
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        average amount of acquisition indebtedness
                      </strong>{' '}
                      on, or allocable to, the debt-financed property for the
                      tax year.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Use the required federal Form 990-T calculation method to
                      determine the average acquisition indebtedness.
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If the
                  calculated average acquisition indebtedness is $600,000 <br />
                  <strong style={{ fontWeight: 600 }}> Enter: $600,000 </strong>
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (e):
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        average adjusted basis
                      </strong>{' '}
                      of the debt-financed property, or the portion of the basis
                      allocable to that property.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Calculate the amount according to the federal Form 990-T
                      instructions and attach the supporting schedule when
                      required.
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If the
                  calculated average adjusted basis is $800,000 <br />
                  <strong style={{ fontWeight: 600 }}> Enter: $800,000 </strong>
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (f):
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the percentage calculated by dividing{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Column (d) by Column (e)
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Calculation:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (d) Ã· Column (e) = Column (f){' '}
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,
                      listStyle: 'none',
                      p: 0,
                      '& li': {
                        mb: 1.5,
                        display: 'flex',
                        gap: 1,
                        ml: 2,
                      },
                    }}
                  >
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (d) = $600,000</Box>
                    </li>
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (e) = $800,000 </Box>
                    </li>
                  </Box>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    $600,000 Ã· $800,000 = 75%{' '}
                  </strong>
                  <br />
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Enter: 75% in Column (f){' '}
                  </strong>
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (g):
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the portion of gross income attributable to the
                      debt-financed property.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Calculate it by multiplying{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Column (b) by Column (f)
                      </strong>
                      .
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Calculation:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (b) Ã— Column (f) = Column (g){' '}
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,
                      listStyle: 'none',
                      p: 0,
                      '& li': {
                        mb: 1.5,
                        display: 'flex',
                        gap: 1,
                        ml: 2,
                      },
                    }}
                  >
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (b) = $100,000</Box>
                    </li>
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (f) = 75%</Box>
                    </li>
                  </Box>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    $100,000 Ã— 75% = $75,000{' '}
                  </strong>
                  <br />
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Enter: 75,000 in Column (g){' '}
                  </strong>
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (h):
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the deductions allocable to the debt-financed
                      portion of the property.
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Calculate this amount by adding{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Column (c)(i) and Column (c)(ii){' '}
                      </strong>{' '}
                      and multiplying the result by{' '}
                      <strong style={{ fontWeight: 600 }}> Column (f)</strong>.
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Calculation:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  [Column (c)(i) + Column (c)(ii)] Ã— Column (f) = Column (h)
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <Box
                    component="ul"
                    sx={{
                      mb: 3,
                      listStyle: 'none',
                      p: 0,
                      '& li': {
                        mb: 1.5,
                        display: 'flex',
                        gap: 1,
                        ml: 2,
                      },
                    }}
                  >
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (c)(i) = $20,000 </Box>
                    </li>
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (c)(ii) = $10,000</Box>
                    </li>
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (f) = 75% </Box>
                    </li>
                  </Box>
                  First: <br />
                  $20,000 + $10,000 = $30,000 <br />
                  Then: <br />
                  $30,000 Ã— 75% = $22,500
                  <br />
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Enter: 22,500 in Column (h)
                  </strong>
                </Alert>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Column (i):
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the net income or loss includible from the debt-financed
                  property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Calculate it by subtracting{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Column (h) from Column (g)
                  </strong>
                  .
                </Typography>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong>
                  <Box
                    component="ul"
                    sx={{
                      mb: 0,
                      listStyle: 'none',
                      p: 0,
                      '& li': {
                        mb: 1.5,
                        display: 'flex',
                        gap: 1,
                        ml: 2,
                      },
                    }}
                  >
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (g) = $75,000 </Box>
                    </li>
                    <li>
                      <CheckIcon fontSize="small" />
                      <Box>Column (h) = $22,500 </Box>
                    </li>
                  </Box>

                  <br />
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    $75,000  ˆ’ $22,500 = $52,500
                  </strong>
                  <br />
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Enter: 52,500 in Column (i)
                  </strong>
                </Alert>

                <Typography
                  variant="body1"
                  sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                >
                  Line1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the information and calculations for your{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    first debt-financed property.
                  </strong>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                >
                  Line2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the information and calculations for your{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    second debt-financed property.
                  </strong>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                >
                  Line3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the information and calculations for your{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    third debt-financed property.
                  </strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For each property, complete the applicable columns from{' '}
                  <strong style={{ fontWeight: 600 }}> (a) through (i).</strong>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                >
                  Line4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add Column (i) amounts and carry the total to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Form 109, Part I, Line 7
                  </strong>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="sche"
                >
                  Schedule E Investment Income of an R&TC Section 23701g,
                  Section 23701i, or Section 23701n Organization
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shce-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shce-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shce-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule E – Investment Income, investment income, deductions, and balance."
                    width={760}
                    height={107}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (a): Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter a description of each investment income source. Include
                  details such as the type of investment, security, or
                  income-producing asset that generated the income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (b): Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of investment income received from each
                  source during the tax year. Include applicable income from
                  securities and other similar investments.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (c): Deductions directly connected (attach schedule)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter expenses directly related to earning the reported
                  investment income. Attach a supporting schedule explaining the
                  deductions included.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d): Net investment income, column (b) less column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract the deductions in Column (c) from the investment
                  income reported in Column (b). Enter the resulting net
                  investment income amount.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e): Set-asides (attach schedule)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of investment income set aside for purposes
                  allowed under California tax rules. Attach a schedule showing
                  how the set-aside amount was calculated.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f): Balance of investment income, column (d) less
                  column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract the set-aside amount in Column (e) from the net
                  investment income reported in Column (d). Enter the remaining
                  taxable investment income balance.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3 – Total Investment Income
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Enter the total amount and carry it to{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Side 2, Part I, Line 8
                      </strong>{' '}
                      of the return.{' '}
                    </Box>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4 – Gross Income From Members
                </Typography>
                <Typography variant="body1">
                  Enter gross income received from members, including:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Member dues</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Membership fees</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Charges</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Similar member-related amounts</Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Do not include nonmember investment income on this line.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schf"
                >
                  Schedule F Interest, Annuities, Royalties and Rents from
                  Controlled Organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shcf-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shcf-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shcf-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule F – Interest, Annuities, Royalties and Rents from Controlled Organizations, lines 1–6: controlled organizations, income, payments, and deductions."
                    width={760}
                    height={286}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  Exempt Controlled Organizations:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (a): Name of controlled organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the legal name of each controlled exempt organization
                  from which income was received.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (b): Employer identification number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the EIN of the controlled organization.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (c): Net unrelated income (loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the controlled organization's net unrelated income or
                  loss related to the reported transaction.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (d): Total of specified payments made
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total payments made by the controlling organization
                  to the controlled organization, including interest, annuities,
                  royalties, rents, or other specified payments.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (e): Part of column (d) that is included in the controlling
                  organization’s gross income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the portion of payments from Column (d) that must be
                  included in the controlling organization's gross income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (f): Deductions directly connected with income in column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter expenses directly connected with the income reported in
                  Column (e).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    mb: 0.8,
                  }}
                >
                  Nonexempt Controlled Organizations
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (g): Taxable income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the taxable income of the nonexempt controlled
                  organization.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (h): Net unrelated income (loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the net unrelated income or loss reported by the
                  nonexempt controlled organization.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (I): Total of specified payments made
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of specified payments made between
                  organizations.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (j): Part of column (i) that is included in the controlling
                  organization’s gross income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the portion of payments included in the controlling
                  organization's gross income.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  (k): Deductions directly connected with income in column (j)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter deductions directly related to the income reported in
                  Column (j).
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,

                    mb: 0.8,
                  }}
                >
                  Line 4 – Total Included Gross Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Add the amounts reported in:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Column (e) for exempt controlled organizations</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Column (j) for nonexempt controlled organizations</Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the combined total on Line 4.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,

                    mb: 0.8,
                  }}
                >
                  Line 5 – Total Directly Connected Deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Add the amounts reported in:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Column (f)</Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>Column (k)</Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total deductions directly connected with the
                  reported income.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,

                    mb: 0.8,
                  }}
                >
                  Line 6 – Net Income from Controlled Organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtract Line 5 from Line 4.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the resulting amount on:{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Side 2, Part I, Line 9{' '}
                  </strong>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schg"
                >
                  Schedule G - Exploited Exempt Activity Income, Other Than
                  Advertising Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shcg-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shcg-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shcg-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule G – Exploited Exempt Activity Income, lines 1–5: exempt activities, income, expenses, and net income."
                    width={760}
                    height={165}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (a):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter a description of the exempt activity that is being
                  exploited through an unrelated trade or business. If more than
                  one unrelated activity exploits the same exempt activity,
                  attach a separate schedule identifying the additional
                  activities.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (b):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    gross income from the unrelated trade or business
                  </strong>{' '}
                  that exploits the exempt activity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (c):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the expenses that are{' '}
                  <strong style={{ fontWeight: 600 }}>
                    directly connected with producing the unrelated business
                    income reported in Column (b).
                  </strong>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (d):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Column (c) from Column (b)
                  </strong>
                  . Enter the resulting net income from the unrelated trade or
                  business.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (e):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    gross income generated from the activity that is not treated
                    as unrelated business income
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (f):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the expenses attributable to the income reported in{' '}
                  <strong style={{ fontWeight: 600 }}>Column (e)</strong>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (g):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Column (e) from Column (f)
                  </strong>
                  . Enter the excess exempt expense, but{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    do not enter an amount greater than Column (d)
                  </strong>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (h):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Column (g) from Column (d)
                  </strong>
                  . If the result is less than zero, enter{' '}
                  <strong style={{ fontWeight: 600 }}>zero</strong> rather than
                  a negative amount.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 5:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,
                    listStyle: 'none',
                    p: 0,
                    '& li': {
                      mb: 1.5,
                      display: 'flex',
                      gap: 1,
                      ml: 2,
                    },
                  }}
                >
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      Add the amounts reported for each activity and enter the
                      applicable total on{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Schedule G, Line 5
                      </strong>
                      .{' '}
                    </Box>
                  </li>
                  <li>
                    <CheckIcon fontSize="small" />
                    <Box>
                      The amount from Schedule G, Line 5, is carried to{' '}
                      <strong style={{ fontWeight: 600 }}>
                        {' '}
                        Form 109, Side 2, Part I, Line 10
                      </strong>
                      .{' '}
                    </Box>
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{
                    mt: 0,
                    mb: 3,
                    backgroundColor: '#f6e6ea',
                    borderLeft: '3px solid #c12654',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> <br />
                  Suppose an organization operates an exempt activity that is
                  exploited through an unrelated business:
                  <Box
                    component="ul"
                    sx={{
                      mb: 0,

                      p: 0,
                      '& li': {
                        mb: 1.5,
                        // display: 'flex',
                        gap: 1,
                        ml: 3,
                      },
                    }}
                  >
                    <li>
                      <Box>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Column (b):
                        </strong>{' '}
                        $50,000 gross unrelated business income{' '}
                      </Box>
                    </li>
                    <li>
                      <Box>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Column (c):
                        </strong>{' '}
                        $20,000 directly connected expenses
                      </Box>
                    </li>
                    <li>
                      <Box>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Column (d):
                        </strong>{' '}
                        $30,000 net unrelated business income
                      </Box>
                    </li>
                    <li>
                      <Box>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Column (e):
                        </strong>{' '}
                        $12,000 gross income that is not unrelated business
                        income
                      </Box>
                    </li>
                    <li>
                      <Box>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Column (f):
                        </strong>{' '}
                        $18,000 expenses attributable to Column (e)
                      </Box>
                    </li>
                    <li>
                      <Box>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Column (g):
                        </strong>{' '}
                        $6,000 excess exempt expense
                      </Box>
                    </li>
                    <li>
                      <Box>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Column (h):
                        </strong>{' '}
                        $24,000 net income includible
                      </Box>
                    </li>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {' '}
                    The calculation is:{' '}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      $50,000  ˆ’ $20,000 = $30,000
                    </strong>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      $18,000  ˆ’ $12,000 = $6,000
                    </strong>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      $30,000  ˆ’ $6,000 = $24,000
                    </strong>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Therefore,{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      $24,000 is the amount reported as net income includible in
                      Column (h).
                    </strong>
                  </Typography>
                </Alert>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Important:</strong>{' '}
                  Schedule G is for exploited exempt activity income{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    other than advertising income
                  </strong>
                  . Advertising income is reported separately on{' '}
                  <strong style={{ fontWeight: 600 }}> Schedule H.</strong>
                  California FTB states that Schedule G generally follows
                  federal law.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schh"
                >
                  Schedule H - Advertising Income and Excess Advertising Costs
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use <strong style={{ fontWeight: 600 }}>Schedule H</strong> to
                  report advertising income and determine whether the
                  organization has{' '}
                  <strong style={{ fontWeight: 600 }}>
                    net advertising income or excess advertising costs
                  </strong>{' '}
                  from its periodicals.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shch-part1-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shch-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shch-part1-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule H – Advertising Income and Excess Advertising Costs, Parts I: advertising income, costs, and net advertising income."
                    width={760}
                    height={216}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Income from Periodicals Reported on a Consolidated
                  Basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete Part I when the organization reports income from
                  multiple periodicals on a{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    consolidated basis
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (a):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of each periodical whose advertising income is
                  being reported.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (b):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total gross advertising income received or earned
                  from the periodical.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (c):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the expenses directly related to producing the
                  advertising income reported in Column (b).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (d):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Compare Column (b) with Column (c). If gross advertising
                  income is greater than direct advertising costs, subtract
                  Column (c) from Column (b) and enter the advertising income.
                  If Column (c) is greater than Column (b), report the excess
                  advertising costs in{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Part III, Column B
                  </strong>{' '}
                  and do not complete Columns (e), (f), and (g).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (e):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Column (b) is greater than Column (c), enter the income
                  received from circulation of the periodical.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (f):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the costs attributable to producing or maintaining
                  readership for the periodical.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (g):
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If circulation income in Column (e) is greater than readership
                  costs in Column (f), enter the amount from Column (d). If
                  Column (f) is greater than Column (e), calculate the amount
                  using the formula provided on the form and enter the result.
                  If the result is less than zero, enter zero.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The 2025 form specifically directs taxpayers to move excess
                  advertising costs to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Part III, Column B,
                  </strong>{' '}
                  while net advertising income is carried to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Part III, Column A.
                  </strong>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts reported in the applicable columns for Part I
                  and enter the totals on{' '}
                  <strong style={{ fontWeight: 600 }}> Line 4</strong>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Income from Periodicals Reported on a Separate Basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use Part II when income from a periodical is not reported on a
                  consolidated basis.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shch-part2-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shch-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shch-part2-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule H – Advertising Income and Excess Advertising Costs, Parts II: advertising income, costs, and net advertising income."
                    width={760}
                    height={66}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 5-7:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Complete Lines{' '}
                  <strong style={{ fontWeight: 600 }}> 5 through 7</strong> for
                  the applicable periodicals.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Column A: Net Advertising Income
                </Typography>

                <picture>
                  <source srcSet="/assets/images/ftbf109-shch-part3-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shch-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shch-part3-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule H – Advertising Income and Excess Advertising Costs, Parts III: advertising income, costs, and net advertising income."
                    width={760}
                    height={115}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (a):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter{' '}
                  <strong style={{ fontWeight: 600 }}>
                    “consolidated periodical”
                  </strong>{' '}
                  and/or the names of the non-consolidated periodicals whose net
                  advertising income is being reported.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (b):
                </Typography>
                <Typography variant="body1">Enter:</Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The total amounts from{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Part I, Column (d) or Column (g),
                    </strong>{' '}
                    as applicable; and
                  </li>
                  <li>
                    The applicable amounts from{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Part II, Column (d) or Column (g)
                    </strong>
                    .
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The total from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Part III, Column A
                  </strong>{' '}
                  is carried to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Form 109, Side 2, Part I, Line 11
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the applicable{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    net advertising income amounts
                  </strong>{' '}
                  reported in Part III, Column A. Enter the total on{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule H, Line 4
                  </strong>{' '}
                  and also carry the same amount to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Side 2, Part I, Line 11
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Column B: Excess Advertising Costs
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (a) – Periodical Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter “consolidated periodical” and/or the names of the
                  non-consolidated periodicals for which excess advertising
                  costs are being reported.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (b) – Excess Advertising Costs
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the total excess advertising costs from:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Part I, Column (d), when direct advertising costs exceed
                    gross advertising income; and
                  </li>
                  <li>Applicable amounts from Part II, Column (d).</li>
                  <li>
                    The total from Part III, Column B is carried to Form 109,
                    Side 2, Part II, Line 27.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 5:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the applicable{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    excess advertising costs
                  </strong>{' '}
                  reported in Part III, Column B. Enter the total on{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule H, Line 5
                  </strong>{' '}
                  and also carry the same amount to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Side 2, Part II, Line 27
                  </strong>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schi"
                >
                  Schedule I - Compensation of Officers, Directors, and Trustees
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use <strong style={{ fontWeight: 600 }}> Schedule I</strong>{' '}
                  to calculate the portion of compensation paid to an
                  organization's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    officers, directors, and trustees
                  </strong>{' '}
                  that is attributable to its unrelated business activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The total from Schedule I is entered on{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Form 109, Side 2, Part II, Line 14
                  </strong>
                  .{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shci-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shci-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shci-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule I – Compensation of Officers, Directors, and Trustees, lines 1–6: names, titles, compensation, and percentage of time."
                    width={760}
                    height={140}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (a):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of each officer, director, or trustee who
                  received compensation that is attributable to the
                  organization's unrelated business activities.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (b):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the person's title or position within the organization,
                  such as President, Treasurer, Director, or Trustee.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (c):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the percentage of the individual's working time devoted
                  to the organization's unrelated business activities. Enter the
                  percentage as shown on the form.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (d):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the portion of the individual's compensation that is
                  attributable to the unrelated business activity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 6:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Add the amounts reported in{' '}
                    <strong style={{ fontWeight: 600 }}> Column (d)</strong> for
                    all listed individuals.
                  </li>
                  <li>
                    Enter the total on form 109, Side 2, Part II, Line 14 -
                    Compensation of officers, directors, and trustees.
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="info"
                  sx={{
                    mt: 0,
                    mb: 3,

                    borderLeft: '3px solid #0288d1',
                    color: '#111',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>
                    Important Point for Schedule I{' '}
                  </strong>
                  <br /> Do not automatically enter an individual's{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    entire compensation
                  </strong>
                  . Schedule I is used to determine the compensation
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    attributable to the unrelated business activity.
                  </strong>{' '}
                  The FTB instructions specifically state that Schedule I is
                  completed for the amount reported on Form 109, Part II, Line
                  14.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schj"
                >
                  Schedule J Depreciation (Corporations and Associations only.
                  Trusts use form FTB 3885F)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Schedule J</strong> is
                  used by{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    corporations and associations
                  </strong>{' '}
                  to report depreciation expenses related to property used in an
                  unrelated trade or business.{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Trusts should use FTB Form 3885F instead
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use the applicable row and columns to report depreciation by
                  type of property. The final depreciation amount from Schedule
                  J is carried to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Side 2, Part II, Line 21a
                  </strong>
                  .
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftbf109-shcj-img.webp" />
                  <source srcSet="/assets/images/ftbf109-shcj-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftbf109-shcj-img.png"
                    loading="lazy"
                    alt="FTB Form 109 Schedule J – Depreciation, lines 1–6: depreciation, property, costs, methods, and amounts claimed."
                    width={760}
                    height={294}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (a):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the type or description of the depreciable property. Use
                  the applicable category, such as buildings, furniture and
                  fixtures, transportation equipment, machinery and other
                  equipment, or specify another type of property.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (b):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date the property was acquired, using the
                  (dd/mm/yyyy) format shown on the form.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (c):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the property's cost or other applicable tax basis used
                  to calculate depreciation.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (d):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total depreciation allowed or allowable for the
                  property in prior tax years.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (e):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the depreciation method used to calculate the property's
                  depreciation, such as the applicable method under the tax
                  rules.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (f):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the applicable recovery period, useful life, or
                  depreciation rate used for the property.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Column (g):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the depreciation deduction for the current tax year for
                  the property.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 1:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      total additional first-year depreciation
                    </strong>{' '}
                    for the tax year.
                  </li>
                  <li>
                    Do <strong style={{ fontWeight: 600 }}>not</strong> include
                    this amount in the individual property amounts reported on
                    the lines below.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report depreciation for the applicable categories of property
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 2a:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter depreciation for buildings.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 2b:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter depreciation for furniture and fixtures.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 2c:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter depreciation for transportation equipment.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 2d:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter depreciation for machinery and other equipment.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 2e:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Specify the type of property and enter the applicable
                  depreciation amount for other depreciable property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For each applicable property category, complete the required
                  columns from{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Column (b) through Column (g)
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter depreciation that does not belong in the property
                  categories listed on Lines 2a through 2e.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the applicable depreciation amounts reported above and
                  enter the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    total depreciation for Schedule J
                  </strong>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 5:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any depreciation amount that has{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      already been claimed elsewhere on the return
                    </strong>
                    .
                  </li>
                  <li>Do not include the same depreciation amount twice. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Line 6:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Line 5 from Line 4
                    </strong>
                    .{' '}
                  </li>
                  <li>
                    Enter the resulting balance on{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule J, Line 6,
                    </strong>{' '}
                    and also enter the same amount on{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Side 2, Part II, Line 21a
                    </strong>
                    .
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 3 }}>
                  Calculation: Line 4  ˆ’ Line 5 = Line 6.
                </Typography>

                <Alert
                  severity="info"
                  icon={false}
                  id="sources"
                  sx={{ borderRadius: 2, scrollMarginTop: 88 }}
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on the official California
                        Franchise Tax Board (FTB){' '}
                        <Link
                          href="https://www.ftb.ca.gov/forms/2025/2025-109-booklet.html"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Instructions
                        </Link>{' '}
                        for CA Form 109. For the most accurate and up-to-date
                        filing requirements, deadlines, and guidance, refer to
                        the official FTB website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Your CA Form 109?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              This guide provides step-by-step instructions for completing CA
              Form 109 and
              <Hidden smDown>
                {' '}
                <br />{' '}
              </Hidden>{' '}
              explains the applicable filing requirements and due dates.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=caform109instructions`}
              >
                File Your CA Form 109 Online
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default CAForm109Instructions;
